import React, { useState, useCallback, useEffect } from "react";
import { LaunchpadLayout } from "@/components/LaunchpadLayout";
import { NavList } from "../User";
import { i18n } from "@/locales";
import { MobileLaunchpadLayout } from "@/components/MobileLaunchpadLayout";
import { useMediaSize } from "@/hooks/useMediaSize";
import Axios from "axios";
import { AppInfo } from "./types";
import { MobileAddApp } from "./MobileAddApp";
import { message } from "antd";
import { useNavigate } from "react-router";
import qs from "query-string";
import { useTranslation } from "react-i18next";
import { MobileAppList } from "./MobileAppList";
import { PCAndPadAppList } from "./PCAndPadAppList";
import { castArray } from "lodash";

export const LaunchpadPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isPhoneMedia } = useMediaSize();
  const [showMobileAddApp, setShowMobileApp] = useState(false);

  const [allApps, setAllApps] = useState<AppInfo[]>([]);
  const [loadingAllApp, setLoadingAllApp] = useState(true);
  const [orderedApps, setOrderedApps] = useState<AppInfo[]>([]);
  const [loadingOrderedApp, setLoadingOrderedApp] = useState(true);

  // 集成应用无权限登录跳转过来
  useEffect(() => {
    const search = window.location.search;
    const query = qs.parse(search);

    if (Number(query.deny_login)) {
      message.error(
        t("common.sorryForNoPermissionLogin", {
          name: query.deny_app_name,
        })
      );
    }
    delete query.deny_login;
    delete query.deny_app_name;

    navigate(`/launchpad?${qs.stringify(query)}`);
  }, [navigate, t]);

  // 所有 app，包括未安装的
  const fetchAllApps = useCallback(async () => {
    setLoadingAllApp(true);
    try {
      const res = await Axios.get<{
        list: any[];
        totalCount: number;
      }>(`/api/v2/users/launchpad/all-applications`);

      setAllApps(res.data.list);
    } finally {
      setLoadingAllApp(false);
    }
  }, []);

  // 获取用户已经安装的 apps
  const fetchOrderedApps = useCallback(async () => {
    setLoadingOrderedApp(true);
    try {
      const res = await Axios.get<{
        list: any[];
        totalCount: number;
        config: {
          uninstallApps: string[];
        };
      }>(`/api/v2/users/launchpad/applications`);

      setOrderedApps(res.data.list);
    } finally {
      setLoadingOrderedApp(false);
    }
  }, []);

  // 初始化
  useEffect(() => {
    if (!window.__user__) {
      window.location.href = "/login?logout=1";
      return;
    }
    fetchAllApps();
    fetchOrderedApps();
  }, [fetchAllApps, fetchOrderedApps]);

  // 移除应用
  const removeAppRequest = useCallback(
    async (id: string | string[]) => {
      try {
        const res: any = await Axios.delete(
          `/api/v2/users/launchpad/apps/?${qs.stringify(
            {
              ids: castArray(id),
            },
            {
              arrayFormat: "bracket",
            }
          )}`
        );
        if (res.code !== 200) {
          message.error(res.message);
        }
      } finally {
        fetchOrderedApps();
      }
    },
    [fetchOrderedApps]
  );

  // 安装应用
  const installAppRequest = useCallback(
    async (addId: string) => {
      let ids = orderedApps.map((i) => i.id);
      if (addId) {
        ids = ids.concat(addId);
      }
      try {
        let res: any = await Axios.post(`/api/v2/users/launchpad/order`, {
          ids: ids,
        });
        if (res.code !== 200) {
          message.error(res.message);
        }
      } finally {
        fetchOrderedApps();
      }
    },
    [fetchOrderedApps, orderedApps]
  );

  // 调整应用顺序
  const sortApps = useCallback(
    async (ids: string[]) => {
      try {
        let res: any = await Axios.post(`/api/v2/users/launchpad/order`, {
          ids: ids,
        });
        if (res.code !== 200) {
          message.error(res.message);
        }
      } finally {
        fetchOrderedApps();
      }
    },
    [fetchOrderedApps]
  );

  const LaunchpadAppsProps = {
    removeAppRequest,
    allApps,
    // TODO 不应该直接把 setState 传入子组件，数据源不清晰
    setOrderedApps,
    fetchOrderedApps,
    orderedApps,
    loadingAllApp,
    loadingOrderedApp,
    installAppRequest,
    sortApps,
  };

  return (
    <NavList.Provider
      value={
        [
          // {
          //   key: "install",
          //   label: (
          //     <>
          //       <IconFont type="authing-tianjia" />
          //       {i18n.t("common.addApp")}
          //     </>
          //   ),
          //   onClick: () => {
          //     setShowMobileApp(true);
          //   },
          // },
        ]
      }
    >
      {!isPhoneMedia ? (
        <LaunchpadLayout>
          <PCAndPadAppList {...LaunchpadAppsProps} />
        </LaunchpadLayout>
      ) : (
          <MobileLaunchpadLayout
            showLangToggle={!showMobileAddApp}
            navToFirstPage={() => setShowMobileApp(false)}
            pageTitle={
              window.__config__?.userpoolName || i18n.t("common.appPanel")
            }
            isFirstPage={!showMobileAddApp}
          >
            {showMobileAddApp ? (
              <MobileAddApp
                removeAppRequest={removeAppRequest}
                installAppRequest={installAppRequest}
                allApps={allApps}
                orderedApps={orderedApps}
              />
            ) : (
                <MobileAppList
                  {...LaunchpadAppsProps}
                  onShowAddApp={() => setShowMobileApp(true)}
                />
              )}
          </MobileLaunchpadLayout>
        )}
    </NavList.Provider>
  );
};
